import { backendClient } from '../lib/api/backendClient';
import { msToTime } from '../utils';
import { worksService } from './works';

import type { LibraryRecording } from '../interfaces/recording';
import type { SimpleArtist } from '../interfaces/artist';

type BackendRecording = Record<string, any>;

export interface RecordingTrackInfo {
  id: string;
  name: string;
  position?: number;
  durationLabel?: string;
  performerName?: string;
  uri?: string;
  spotifyTrackId?: string;
  spotifyUri?: string;
  artists?: SimpleArtist[];
}

export interface RecordingPerformerInfo {
  id?: string;
  name: string;
  role?: string;
}

export interface RecordingWorkSummary {
  id: string;
  name?: string;
  composerName?: string;
  composerId?: string;
  confidence?: number;
  catalogLabel?: string;
  catalogNumberNormalized?: string;
}

export interface RecordingDetail {
  recording: LibraryRecording;
  tracks: RecordingTrackInfo[];
  performers: RecordingPerformerInfo[];
  works: RecordingWorkSummary[];
  description?: string;
  label?: string;
  raw?: BackendRecording;
}

export interface TrackMatchSummary {
  spotifyTrackId: string;
  workId?: string;
  workName?: string;
  composerId?: string;
  composerName?: string;
  spotifyAlbumId?: string;
  trackNumber?: number;
  confidence?: number;
  recordingId?: string;
  recordingName?: string;
  albumName?: string;
  albumImageUrl?: string;
  performerName?: string;
}

export interface FetchComposerRecordingsOptions {
  limit?: number;
  offset?: number;
  composerName?: string;
}

export interface FetchRecordingsOptions {
  limit?: number;
  offset?: number;
  composerName?: string;
  workName?: string;
}

export interface FetchRecordingByIdOptions {
  composerName?: string;
  workId?: string;
  workName?: string;
}

export interface RecordingsPagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface RecordingListResult {
  recordings: LibraryRecording[];
  pagination: RecordingsPagination;
}

const stringOrUndefined = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const numberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const clampPositiveInteger = (value: unknown, { min, max, fallback }: { min: number; max: number; fallback: number }): number => {
  const parsed = numberOrUndefined(value);
  if (typeof parsed === 'number') {
    const normalized = Math.trunc(parsed);
    if (normalized >= min) {
      return Math.min(Math.max(normalized, min), max);
    }
  }
  return fallback;
};

const pickFirstString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    const resolved = stringOrUndefined(value);
    if (resolved) {
      return resolved;
    }
  }
  return undefined;
};

const pickFirstNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    const resolved = numberOrUndefined(value);
    if (typeof resolved === 'number') {
      return resolved;
    }
  }
  return undefined;
};

const toSpotifyTrackUri = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed.length) {
    return undefined;
  }
  if (/^spotify:track:[0-9A-Za-z]{22}$/i.test(trimmed)) {
    return trimmed;
  }
  if (/^[0-9A-Za-z]{22}$/.test(trimmed)) {
    return `spotify:track:${trimmed}`;
  }
  return undefined;
};

const pickSpotifyTrackUri = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    const uri = toSpotifyTrackUri(value);
    if (uri) {
      return uri;
    }
  }
  return undefined;
};

const normalizeYear = (value: unknown): number | undefined => {
  const numeric = numberOrUndefined(value);
  if (typeof numeric === 'number') {
    return Math.trunc(numeric);
  }

  if (typeof value === 'string') {
    const match = value.match(/(\d{4})/);
    if (match) {
      const parsed = Number(match[1]);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }

  return undefined;
};

const deriveDurationLabel = (recording: BackendRecording, durationMsTotal?: number): string | undefined => {
  const explicit = pickFirstString(
    recording.durationLabel,
    recording.duration_label,
    (recording.stats as Record<string, unknown> | undefined)?.durationLabel
  );
  if (explicit) {
    return explicit;
  }

  const durationMs =
    durationMsTotal ??
    pickFirstNumber(recording.durationMsTotal, recording.duration_ms_total, recording.durationTotalMs) ??
    pickFirstNumber(recording.totalDurationMs, recording.total_duration_ms);

  if (typeof durationMs !== 'number' || durationMs <= 0) {
    return undefined;
  }

  const minutes = Math.round(durationMs / 60000);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return undefined;
  }

  return `${minutes} min`;
};

const extractImageUrl = (recording: BackendRecording): string | undefined => {
  const direct = pickFirstString(
    recording.imageUrl,
    recording.image_url,
    recording.albumImageUrl,
    recording.album_image_url,
    recording.coverUrl,
    recording.cover_url,
    recording.thumbnailUrl,
    recording.thumbnail_url
  );

  if (direct) {
    return direct;
  }

  const album = recording.album as Record<string, unknown> | undefined;
  if (album && typeof album === 'object') {
    const albumImage = pickFirstString(
      album.imageUrl,
      album.image_url,
      album.coverUrl,
      album.cover_url,
      album.thumbnailUrl,
      album.thumbnail_url
    );
    if (albumImage) {
      return albumImage;
    }

    const images = album.images;
    if (Array.isArray(images)) {
      for (const entry of images) {
        if (!entry || typeof entry !== 'object') continue;
        const entryUrl = pickFirstString(
          (entry as Record<string, unknown>).url,
          (entry as Record<string, unknown>).src
        );
        if (entryUrl) {
          return entryUrl;
        }
      }
    }
  }

  return undefined;
};

const extractAlbumId = (recording: BackendRecording): string | undefined => {
  const albumId = pickFirstString(
    recording.albumId,
    recording.album_id,
    recording.spotifyAlbumId,
    recording.spotify_album_id
  );
  if (albumId) {
    return albumId;
  }

  const album = recording.album as Record<string, unknown> | undefined;
  if (album && typeof album === 'object') {
    return pickFirstString(
      album.id,
      album.albumId,
      album.spotifyId,
      album.spotify_id,
      album.spotifyAlbumId,
      album.spotify_album_id
    );
  }

  return undefined;
};

const extractAlbumName = (recording: BackendRecording): string | undefined => {
  const albumName = pickFirstString(
    recording.albumName,
    recording.album_name,
    (recording.album as Record<string, unknown> | undefined)?.name
  );
  return albumName;
};

const collectWorkEntries = (recording: BackendRecording): Record<string, any>[] => {
  const entries: Record<string, any>[] = [];
  const pushIfRecord = (value: unknown) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      entries.push(value as Record<string, any>);
    }
  };

  pushIfRecord(recording.work);
  pushIfRecord((recording as Record<string, any>)?.primaryWork);
  pushIfRecord((recording as Record<string, any>)?.primary_work);
  pushIfRecord((recording as Record<string, any>)?.catalogWork);
  pushIfRecord((recording as Record<string, any>)?.catalog_work);

  const collections = [
    recording.works,
    recording.relatedWorks,
    recording.workList,
    recording.work_list,
    recording.matchedWorks,
    recording.matched_works,
  ];

  for (const collection of collections) {
    if (!Array.isArray(collection)) continue;
    for (const entry of collection) {
      pushIfRecord(entry);
    }
  }

  return entries;
};

const extractWorkCatalogMetadata = (
  recording: BackendRecording
): { label?: string; numberNormalized?: string } => {
  let label = pickFirstString(
    recording.workCatalogLabel,
    recording.work_catalog_label,
    recording.catalogLabel,
    recording.catalog_label,
    recording.catalogueLabel,
    recording.catalogue_label
  );

  let numberNormalized = pickFirstString(
    recording.workCatalogNumberNormalized,
    recording.work_catalog_number_normalized,
    recording.catalogNumberNormalized,
    recording.catalog_number_normalized,
    recording.catalogueNumberNormalized,
    recording.catalogue_number_normalized,
    recording.numberNormalized,
    recording.number_normalized,
    recording.baseNumber,
    recording.base_number
  );

  const workEntries = collectWorkEntries(recording);

  for (const entry of workEntries) {
    if (!label) {
      label =
        pickFirstString(
          entry.catalogLabel,
          entry.catalog_label,
          entry.catalogDisplay,
          entry.catalog_display,
          entry.displayLabel,
          entry.display_label
        ) ?? label;
    }

    if (!numberNormalized) {
      numberNormalized =
        pickFirstString(
          entry.catalogNumberNormalized,
          entry.catalog_number_normalized,
          entry.catalogueNumberNormalized,
          entry.catalogue_number_normalized,
          entry.numberNormalized,
          entry.number_normalized,
          entry.baseNumber,
          entry.base_number
        ) ?? numberNormalized;
    }

    if (!label) {
      const nestedDisplay = [
        entry.catalog,
        entry.catalogue,
        entry.primaryCatalog,
        entry.primary_catalog,
        entry.displayCatalog,
        entry.display_catalog,
      ].find((value) => value && typeof value === 'object');
      if (nestedDisplay && typeof nestedDisplay === 'object') {
        label =
          pickFirstString(
            (nestedDisplay as Record<string, any>).displayLabel,
            (nestedDisplay as Record<string, any>).display_label,
            (nestedDisplay as Record<string, any>).catalogLabel,
            (nestedDisplay as Record<string, any>).catalog_label,
            (nestedDisplay as Record<string, any>).catalogDisplay,
            (nestedDisplay as Record<string, any>).catalog_display
          ) ?? label;
      }
    }

    if (!numberNormalized) {
      const nestedCatalog = [
        entry.catalog,
        entry.catalogue,
        entry.primaryCatalog,
        entry.primary_catalog,
        entry.displayCatalog,
        entry.display_catalog,
      ].find((value) => value && typeof value === 'object');
      if (nestedCatalog && typeof nestedCatalog === 'object') {
        numberNormalized =
          pickFirstString(
            (nestedCatalog as Record<string, any>).numberNormalized,
            (nestedCatalog as Record<string, any>).number_normalized,
            (nestedCatalog as Record<string, any>).number,
            (nestedCatalog as Record<string, any>).no,
            (nestedCatalog as Record<string, any>).value
          ) ?? numberNormalized;
      }
    }

    if (label && numberNormalized) {
      break;
    }
  }

  return {
    label: label ?? undefined,
    numberNormalized: numberNormalized ?? undefined,
  };
};

const extractPerformerName = (recording: BackendRecording): string | undefined => {
  const performer = pickFirstString(
    recording.performerSummary,
    recording.performer_summary,
    recording.performerName,
    recording.performer_name,
    recording.primaryPerformer,
    recording.primary_performer
  );
  if (performer) {
    return performer;
  }

  const performerList = recording.performers;
  if (Array.isArray(performerList)) {
    for (const entry of performerList) {
      if (entry && typeof entry === 'object') {
        const resolved = pickFirstString(
          (entry as Record<string, unknown>).name,
          (entry as Record<string, unknown>).displayName,
          (entry as Record<string, unknown>).display_name
        );
        if (resolved) {
          return resolved;
        }
      } else {
        const resolved = stringOrUndefined(entry);
        if (resolved) {
          return resolved;
        }
      }
    }
  }

  const album = recording.album as Record<string, unknown> | undefined;
  if (album && typeof album === 'object') {
    const artists = album.artists;
    if (Array.isArray(artists)) {
      for (const artist of artists) {
        if (artist && typeof artist === 'object') {
          const resolved = pickFirstString(
            (artist as Record<string, unknown>).name,
            (artist as Record<string, unknown>).displayName,
            (artist as Record<string, unknown>).display_name
          );
          if (resolved) {
            return resolved;
          }
        }
      }
    }
  }

  return undefined;
};

const extractWorkIds = (recording: BackendRecording): string[] => {
  const directIds = Array.isArray((recording as Record<string, unknown>).workIds)
    ? ((recording as Record<string, unknown>).workIds as unknown[])
    : Array.isArray((recording as Record<string, unknown>).work_ids)
      ? ((recording as Record<string, unknown>).work_ids as unknown[])
      : null;

  if (Array.isArray(directIds)) {
    const normalized = directIds
      .map((entry) => stringOrUndefined(entry))
      .filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
    if (normalized.length > 0) {
      return normalized;
    }
  }

  const works = recording.works;
  if (!Array.isArray(works)) {
    const singleWork = recording.work;
    const singleId = pickFirstString(
      (singleWork as Record<string, unknown> | undefined)?.id,
      (singleWork as Record<string, unknown> | undefined)?.workId,
      recording.workId,
      recording.work_id
    );
    return singleId ? [singleId] : [];
  }

  const ids: string[] = [];
  for (const work of works) {
    if (!work || typeof work !== 'object') {
      const resolved = stringOrUndefined(work);
      if (resolved) {
        ids.push(resolved);
      }
      continue;
    }
    const record = work as Record<string, unknown>;
    const workId = pickFirstString(record.id, record.workId, record.work_id);
    if (workId) {
      ids.push(workId);
    }
  }

  return ids;
};

const extractWorkName = (recording: BackendRecording): string | undefined => {
  const direct = pickFirstString(
    recording.workName,
    recording.work_name,
    (recording.work as Record<string, unknown> | undefined)?.name,
    (recording.work as Record<string, unknown> | undefined)?.title,
    (recording.work as Record<string, unknown> | undefined)?.displayName,
    (recording.work as Record<string, unknown> | undefined)?.display_name
  );
  if (direct) {
    return direct;
  }

  const works = recording.works;
  if (Array.isArray(works)) {
    for (const work of works) {
      if (work && typeof work === 'object') {
        const resolved = pickFirstString(
          (work as Record<string, unknown>).name,
          (work as Record<string, unknown>).title,
          (work as Record<string, unknown>).workName,
          (work as Record<string, unknown>).work_name,
          (work as Record<string, unknown>).displayTitle,
          (work as Record<string, unknown>).display_title,
          (work as Record<string, unknown>).preferredTitle,
          (work as Record<string, unknown>).preferred_title,
          ((work as Record<string, unknown>).work as Record<string, unknown> | undefined)?.name
        );
        if (resolved) {
          return resolved;
        }
      }
    }
  }

  return undefined;
};

const extractComposerName = (
  recording: BackendRecording,
  context: { composerName?: string }
): string | undefined => {
  const direct = pickFirstString(
    recording.composerName,
    recording.composer_name,
    recording.composerLabel,
    recording.composer_label
  );
  if (direct) {
    return direct;
  }

  const composer = recording.composer;
  if (composer && typeof composer === 'object') {
    const resolved = pickFirstString(
      (composer as Record<string, unknown>).name,
      (composer as Record<string, unknown>).displayName,
      (composer as Record<string, unknown>).display_name,
      (composer as Record<string, unknown>).fullName,
      (composer as Record<string, unknown>).full_name
    );
    if (resolved) {
      return resolved;
    }
  }

  const works = recording.works;
  if (Array.isArray(works)) {
    for (const work of works) {
      if (!work || typeof work !== 'object') {
        continue;
      }
      const composerInWork = pickFirstString(
        (work as Record<string, unknown>).composerName,
        (work as Record<string, unknown>).composer_name
      );
      if (composerInWork) {
        return composerInWork;
      }

      const composerObj = (work as Record<string, unknown>).composer;
      if (composerObj && typeof composerObj === 'object') {
        const resolved = pickFirstString(
          (composerObj as Record<string, unknown>).name,
          (composerObj as Record<string, unknown>).displayName,
          (composerObj as Record<string, unknown>).display_name
        );
        if (resolved) {
          return resolved;
        }
      }
    }
  }

  return context.composerName;
};

const extractComposerId = (recording: BackendRecording): string | undefined => {
  const direct = pickFirstString(
    recording.composerId,
    recording.composer_id,
    recording.composerUuid,
    recording.composer_uuid,
    recording.composerSlug,
    recording.composer_slug,
    recording.composerSpotifyId,
    recording.composer_spotify_id,
    recording.composerSpotifyArtistId,
    recording.composer_spotify_artist_id
  );
  if (direct) {
    return direct;
  }

  const composer = recording.composer;
  if (composer && typeof composer === 'object') {
    const resolved = pickFirstString(
      (composer as Record<string, unknown>).id,
      (composer as Record<string, unknown>).composerId,
      (composer as Record<string, unknown>).composer_id,
      (composer as Record<string, unknown>).uuid,
      (composer as Record<string, unknown>).slug,
      (composer as Record<string, unknown>).spotifyArtistId,
      (composer as Record<string, unknown>).spotify_artist_id
    );
    if (resolved) {
      return resolved;
    }
  }

  const works = recording.works;
  if (Array.isArray(works)) {
    for (const work of works) {
      if (!work || typeof work !== 'object') {
        continue;
      }

      const fromWork = pickFirstString(
        (work as Record<string, unknown>).composerId,
        (work as Record<string, unknown>).composer_id,
        (work as Record<string, unknown>).composerUuid,
        (work as Record<string, unknown>).composer_uuid,
        (work as Record<string, unknown>).composerSlug,
        (work as Record<string, unknown>).composer_slug
      );
      if (fromWork) {
        return fromWork;
      }

      const composerInWork = (work as Record<string, unknown>).composer;
      if (composerInWork && typeof composerInWork === 'object') {
        const resolved = pickFirstString(
          (composerInWork as Record<string, unknown>).id,
          (composerInWork as Record<string, unknown>).composerId,
          (composerInWork as Record<string, unknown>).composer_id,
          (composerInWork as Record<string, unknown>).uuid,
          (composerInWork as Record<string, unknown>).slug,
          (composerInWork as Record<string, unknown>).spotifyArtistId,
          (composerInWork as Record<string, unknown>).spotify_artist_id
        );
        if (resolved) {
          return resolved;
        }
      }
    }
  }

  return undefined;
};

const extractReleaseYear = (recording: BackendRecording): string | undefined => {
  const releaseYear =
    normalizeYear(recording.releaseYear) ??
    normalizeYear(recording.release_year) ??
    normalizeYear((recording.album as Record<string, unknown> | undefined)?.releaseYear) ??
    normalizeYear((recording.album as Record<string, unknown> | undefined)?.release_year) ??
    normalizeYear((recording.album as Record<string, unknown> | undefined)?.releaseDate);

  if (typeof releaseYear === 'number') {
    return String(releaseYear);
  }

  return undefined;
};

const extractTrackCount = (recording: BackendRecording): number | undefined => {
  const stats = (recording.stats ?? null) as Record<string, unknown> | null;
  const direct = pickFirstNumber(
    recording.trackCount,
    recording.track_count,
    stats?.trackCount,
    stats?.track_count
  );

  if (typeof direct === 'number' && direct > 0) {
    return Math.trunc(direct);
  }

  if (Array.isArray(recording.tracks) && recording.tracks.length > 0) {
    return recording.tracks.length;
  }

  const trackIds = (recording as Record<string, unknown>).trackIds;
  if (Array.isArray(trackIds) && trackIds.length > 0) {
    return trackIds.length;
  }

  return undefined;
};

const buildRecordingId = (recording: BackendRecording): string | undefined => {
  const candidate = pickFirstString(
    recording.id,
    recording.recordingId,
    recording.recording_id,
    recording.uuid,
    recording.slug,
    recording.catalogRecordingId,
    recording.catalog_recording_id
  );
  if (candidate) {
    return candidate;
  }

  const meta = recording._meta;
  if (meta && typeof meta === 'object') {
    const rowId = pickFirstString(
      (meta as Record<string, unknown>).rowId,
      (meta as Record<string, unknown>).row_id
    );
    if (rowId) {
      return rowId;
    }
  }

  return undefined;
};

const normalizeRecording = (
  recording: BackendRecording,
  context: { composerName?: string }
): LibraryRecording => {
  const randomId = (() => {
    const possibleCrypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (possibleCrypto && typeof possibleCrypto.randomUUID === 'function') {
      return possibleCrypto.randomUUID();
    }
    return `recording-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  })();

  const id = buildRecordingId(recording) ?? randomId;
  let spotifyTrackId = pickFirstString(
    recording.spotifyTrackId,
    recording.spotify_track_id,
    (recording as Record<string, unknown>).trackSpotifyId,
    (recording as Record<string, unknown>).track_spotify_id,
    recording.spotifyId,
    recording.spotify_id
  );

  let spotifyUri =
    pickSpotifyTrackUri(
      recording.spotifyUri,
      recording.spotify_uri,
      (recording as Record<string, unknown>).spotifyTrackUri,
      (recording as Record<string, unknown>).spotify_track_uri,
      (recording as Record<string, unknown>).trackUri,
      (recording as Record<string, unknown>).track_uri,
      recording.uri,
      spotifyTrackId
    ) ?? undefined;

  if (!spotifyTrackId && spotifyUri) {
    const match = spotifyUri.match(/^spotify:track:([0-9A-Za-z]{22})$/i);
    if (match) {
      spotifyTrackId = match[1];
    }
  }

  if ((!spotifyTrackId || !spotifyUri) && Array.isArray((recording as Record<string, unknown>).tracks)) {
    const tracks = (recording as { tracks?: unknown[] }).tracks ?? [];
    const resolveTrackSpotifyValues = (track: Record<string, any>) => {
      const trackUri = pickSpotifyTrackUri(
        track.spotifyUri,
        track.spotify_uri,
        track.uri,
        track.spotifyTrackUri,
        track.spotify_track_uri,
        track.spotifyTrackId,
        track.spotify_track_id,
        track.id
      );

      let trackId =
        pickFirstString(
          track.spotifyTrackId,
          track.spotify_track_id
        ) ?? null;

      if (!trackId) {
        const idCandidate = pickFirstString(track.id);
        if (idCandidate && /^[0-9A-Za-z]{22}$/.test(idCandidate)) {
          trackId = idCandidate;
        }
      }

      if (!trackId && trackUri) {
        const match = trackUri.match(/^spotify:track:([0-9A-Za-z]{22})$/i);
        if (match) {
          trackId = match[1];
        }
      }

      return { trackUri, trackId };
    };

    for (const entry of tracks) {
      if (!entry || typeof entry !== 'object') continue;
      const { trackUri, trackId } = resolveTrackSpotifyValues(entry as Record<string, any>);

      if (!spotifyUri && trackUri) {
        spotifyUri = trackUri;
      }
      if (!spotifyTrackId && trackId) {
        spotifyTrackId = trackId;
      }

      if (spotifyUri && spotifyTrackId) {
        break;
      }
    }
  }

  const uri =
    spotifyUri ??
    pickFirstString(recording.uri) ??
    `cmf:recording:${id}`;

  let name = pickFirstString(
    recording.name,
    recording.recordingName,
    recording.title,
    recording.displayName,
    recording.display_name
  );

  const performerName = extractPerformerName(recording);
  const composerName = extractComposerName(recording, context);
  const composerId = extractComposerId(recording);
  const workIds = extractWorkIds(recording);
  const workName = extractWorkName(recording);
  const albumId = extractAlbumId(recording);
  const albumName = extractAlbumName(recording);
  const albumImageUrl = extractImageUrl(recording);
  const year = extractReleaseYear(recording);
  const trackCount = extractTrackCount(recording);
  const durationMsTotal =
    pickFirstNumber(
      recording.durationMsTotal,
      recording.duration_ms_total,
      recording.durationTotalMs,
      recording.totalDurationMs,
      recording.total_duration_ms
    ) ?? undefined;
  const durationLabel = deriveDurationLabel(recording, durationMsTotal);
  const imageUrl = albumImageUrl ?? pickFirstString(recording.imageUrl, recording.image_url);

  if (!name && workName) {
    name = workName;
  }
  if (!name) {
    name = 'Untitled Recording';
  }

  const normalized: LibraryRecording = {
    id,
    uri,
    type: 'recording',
    name,
    performerName: performerName ?? 'Unknown Performer',
    workName: workName ?? name,
    composerName: composerName ?? context.composerName ?? 'Unknown Composer',
    albumId: albumId ?? `cmf-album-${id}`,
    albumName: albumName ?? 'Unknown Album',
  };

  if (year) {
    normalized.year = year;
  }
  if (durationLabel) {
    normalized.durationLabel = durationLabel;
  }
  if (typeof durationMsTotal === 'number' && Number.isFinite(durationMsTotal) && durationMsTotal > 0) {
    normalized.durationMsTotal = durationMsTotal;
  }
  if (imageUrl) {
    normalized.imageUrl = imageUrl;
  }
  if (albumImageUrl) {
    normalized.albumImageUrl = albumImageUrl;
  }
  if (workIds.length > 0) {
    normalized.workId = workIds[0];
  }
  if (typeof trackCount === 'number' && trackCount > 0) {
    normalized.trackCount = trackCount;
  }

  const workCatalogMeta = extractWorkCatalogMetadata(recording);
  if (workCatalogMeta.label) {
    normalized.workCatalogLabel = workCatalogMeta.label;
  }
  if (workCatalogMeta.numberNormalized) {
    normalized.workCatalogNumberNormalized = workCatalogMeta.numberNormalized;
  }

  if (composerId) {
    normalized.composerId = composerId;
  }

  if (spotifyTrackId) {
    normalized.spotifyTrackId = spotifyTrackId;
  }
  if (spotifyUri) {
    normalized.spotifyUri = spotifyUri;
  }

  const artistHash = pickFirstString(
    (recording as Record<string, unknown>).artistHash,
    (recording as Record<string, unknown>).artist_hash,
    (recording as Record<string, unknown>).artistIdsHash,
    (recording as Record<string, unknown>).artist_ids_hash
  );
  if (artistHash) {
    normalized.artistHash = artistHash;
  }

  const avgTrackPopularity = pickFirstNumber(
    (recording as Record<string, unknown>).averageTrackPopularity,
    (recording as Record<string, unknown>).avg_track_popularity
  );
  if (typeof avgTrackPopularity === 'number') {
    normalized.averageTrackPopularity = avgTrackPopularity;
  }

  const proxyPopularity = pickFirstNumber(
    (recording as Record<string, unknown>).proxyPopularity,
    (recording as Record<string, unknown>).proxy_popularity
  );
  if (typeof proxyPopularity === 'number') {
    normalized.proxyPopularity = proxyPopularity;
  }

  return normalized;
};

const deriveTrackDurationLabel = (track: Record<string, any>): string | undefined => {
  const direct = pickFirstString(
    track.durationLabel,
    track.duration_label,
    track.lengthLabel,
    track.length_label,
    track.displayDuration,
    track.display_duration,
    track.duration
  );
  if (direct) {
    return direct;
  }

  const durationMs = pickFirstNumber(
    track.durationMs,
    track.duration_ms,
    track.durationMilliseconds,
    track.duration_milliseconds,
    track.lengthMs,
    track.length_ms,
    track.totalDurationMs,
    track.total_duration_ms,
    track.duration
  );
  if (typeof durationMs === 'number' && durationMs > 0) {
    return msToTime(Math.round(durationMs));
  }

  const durationSeconds = pickFirstNumber(
    track.durationSeconds,
    track.duration_seconds,
    track.lengthSeconds,
    track.length_seconds,
    track.seconds
  );
  if (typeof durationSeconds === 'number' && durationSeconds > 0) {
    return msToTime(Math.round(durationSeconds * 1000));
  }

  const durationMinutes = pickFirstNumber(track.durationMinutes, track.duration_minutes);
  if (typeof durationMinutes === 'number' && durationMinutes > 0) {
    return msToTime(Math.round(durationMinutes * 60 * 1000));
  }

  return undefined;
};

const normalizeTrackList = (recording: BackendRecording): RecordingTrackInfo[] => {
  const normalizeArtistCollection = (value: unknown, fallbackPrefix: string): SimpleArtist[] => {
    if (!value) {
      return [];
    }

    const entries = Array.isArray(value) ? value : [value];
    const seen = new Set<string>();
    const artists: SimpleArtist[] = [];

    entries.forEach((entry, index) => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      const record = entry as Record<string, any>;
      const rawId = stringOrUndefined(
        record.id ??
          record.artistId ??
          record.artist_id ??
          record.spotifyArtistId ??
          record.spotify_artist_id
      );
      const id = rawId?.startsWith('spotify:artist:')
        ? rawId.slice('spotify:artist:'.length)
        : rawId;
      const name = stringOrUndefined(record.name ?? record.displayName ?? record.display_name);
      if (!id && !name) {
        return;
      }
      const resolvedId = id ?? `${fallbackPrefix}-${index}`;
      if (seen.has(resolvedId)) {
        return;
      }
      seen.add(resolvedId);
      const uri =
        stringOrUndefined(record.uri) ??
        (resolvedId && /^[0-9A-Za-z]{22}$/.test(resolvedId) ? `spotify:artist:${resolvedId}` : undefined) ??
        '';
      const href = stringOrUndefined(record.href) ?? '';
      const externalRecord =
        (record.external_urls && typeof record.external_urls === 'object'
          ? (record.external_urls as Record<string, any>)
          : record.externalUrls && typeof record.externalUrls === 'object'
          ? (record.externalUrls as Record<string, any>)
          : {});
      const spotifyUrl = stringOrUndefined(externalRecord.spotify);
      artists.push({
        id: resolvedId,
        name: name ?? resolvedId,
        href,
        type: 'artist',
        uri,
        external_urls: {
          spotify: spotifyUrl ?? '',
        },
      });
    });

    return artists;
  };

  const candidateCollections = [
    recording.tracks,
    recording.trackList,
    recording.track_list,
    recording.tracklist,
    recording.track_listings,
    recording.sections,
    recording.movements,
    recording.movementTracks,
    recording.movement_tracks,
    recording.parts,
    (recording.album as Record<string, any> | undefined)?.tracks,
  ];

  const normalized: RecordingTrackInfo[] = [];
  const seen = new Set<string>();

  for (const collection of candidateCollections) {
    if (!Array.isArray(collection) || !collection.length) {
      continue;
    }

    for (let index = 0; index < collection.length; index += 1) {
      const entry = collection[index];

      if (!entry) {
        continue;
      }

      if (typeof entry !== 'object') {
        const name = stringOrUndefined(entry);
        if (!name) continue;
        const id = `track-${normalized.length + 1}`;
        if (seen.has(id)) continue;
        normalized.push({
          id,
          name,
          position: normalized.length + 1,
        });
        seen.add(id);
        continue;
      }

      const record = entry as Record<string, any>;
      const id =
        pickFirstString(
          record.id,
          record.trackId,
          record.track_id,
          record.uuid,
          record.slug,
          record.isrc,
          record.movementId,
          record.movement_id
        ) ?? `track-${normalized.length + 1}`;

      if (seen.has(id)) {
        continue;
      }

      const name =
        pickFirstString(
          record.name,
          record.title,
          record.displayName,
          record.display_name,
          record.trackName,
          record.track_name,
          record.movementName,
          record.movement_name,
          record.workName,
          record.work_name
        ) ?? `Track ${normalized.length + 1}`;

      const performerName = pickFirstString(
        record.performerName,
        record.performer_name,
        record.artistName,
        record.artist_name,
        record.primaryPerformer,
        record.primary_performer
      );

      const position =
        pickFirstNumber(
          record.position,
          record.index,
          record.trackNumber,
          record.track_number,
          record.movementNumber,
          record.movement_number,
          record.number,
          record.order
        ) ?? normalized.length + 1;

      const durationLabel = deriveTrackDurationLabel(record);
      const spotifyTrackId = pickFirstString(
        record.spotifyTrackId,
        record.spotify_track_id,
        record.trackSpotifyId,
        record.track_spotify_id,
        record.spotifyId,
        record.spotify_id
      );
      const spotifyUri =
        pickSpotifyTrackUri(
          record.spotifyUri,
          record.spotify_uri,
          record.spotifyTrackUri,
          record.spotify_track_uri,
          record.trackUri,
          record.track_uri,
          record.uri,
          spotifyTrackId
        ) ?? undefined;

      const trackInfo: RecordingTrackInfo = {
        id,
        name,
      };

      if (typeof position === 'number') {
        trackInfo.position = position;
      }
      if (durationLabel) {
        trackInfo.durationLabel = durationLabel;
      }
      if (performerName) {
        trackInfo.performerName = performerName;
      }
      if (typeof record.uri === 'string' && record.uri.trim().length) {
        trackInfo.uri = record.uri;
      }
      if (spotifyTrackId) {
        trackInfo.spotifyTrackId = spotifyTrackId;
      }
      if (spotifyUri) {
        trackInfo.spotifyUri = spotifyUri;
        trackInfo.uri = spotifyUri;
      }
      const resolvedArtists = normalizeArtistCollection(
        record.artists ??
          record.spotifyArtists ??
          record.spotify_artists ??
          record.primaryArtists ??
          record.primary_artists,
        `track-artist-${normalized.length}`
      );
      if (resolvedArtists.length) {
        trackInfo.artists = resolvedArtists;
      }

      normalized.push(trackInfo);
      seen.add(id);
    }

    if (normalized.length) {
      break;
    }
  }

  if (!normalized.length) {
    return [];
  }

  return normalized.sort((a, b) => {
    const aPos = typeof a.position === 'number' ? a.position : Number.MAX_SAFE_INTEGER;
    const bPos = typeof b.position === 'number' ? b.position : Number.MAX_SAFE_INTEGER;
    if (aPos === bPos) {
      return a.name.localeCompare(b.name);
    }
    return aPos - bPos;
  });
};

const normalizePerformerCollection = (recording: BackendRecording): RecordingPerformerInfo[] => {
  const sources = [
    recording.performers,
    recording.performerDetails,
    recording.performer_details,
    recording.performerList,
    recording.performer_list,
    recording.artistDetails,
    recording.artist_details,
    recording.artists,
  ];

  const performers: RecordingPerformerInfo[] = [];
  const seen = new Set<string>();

  for (const source of sources) {
    if (!Array.isArray(source)) {
      continue;
    }

    for (const entry of source) {
      if (!entry) {
        continue;
      }

      let performer: RecordingPerformerInfo | null = null;

      if (typeof entry === 'string') {
        const name = stringOrUndefined(entry);
        if (name) {
          performer = { name };
        }
      } else if (typeof entry === 'object') {
        const record = entry as Record<string, any>;
        const name =
          pickFirstString(
            record.name,
            record.displayName,
            record.display_name,
            record.fullName,
            record.full_name,
            record.artistName,
            record.artist_name
          ) ?? undefined;

        if (!name) {
          continue;
        }

        const id = pickFirstString(
          record.id,
          record.performerId,
          record.performer_id,
          record.artistId,
          record.artist_id,
          record.uuid
        );

        let role = pickFirstString(
          record.role,
          record.primaryRole,
          record.primary_role,
          record.instrument,
          record.instrumentName,
          record.instrument_name,
          record.category
        );

        if (!role) {
          const roles = record.roles ?? record.instruments;
          if (Array.isArray(roles)) {
            const labels = roles
              .map((value) => {
                if (!value) return undefined;
                if (typeof value === 'string') {
                  return stringOrUndefined(value);
                }
                if (typeof value === 'object') {
                  const roleRecord = value as Record<string, any>;
                  return (
                    stringOrUndefined(roleRecord.name) ??
                    stringOrUndefined(roleRecord.role) ??
                    stringOrUndefined(roleRecord.label)
                  );
                }
                return undefined;
              })
              .filter((value): value is string => Boolean(value));
            if (labels.length) {
              role = Array.from(new Set(labels)).join(', ');
            }
          }
        }

        performer = { name };
        if (id) {
          performer.id = id;
        }
        if (role) {
          performer.role = role;
        }
      }

      if (!performer) {
        continue;
      }

      const key = `${performer.id ?? performer.name}|${performer.role ?? ''}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      performers.push(performer);
    }
  }

  return performers;
};

const normalizeWorkSummaries = (
  recording: BackendRecording,
  context: { composerName?: string }
): RecordingWorkSummary[] => {
  const works: RecordingWorkSummary[] = [];
  const seen = new Set<string>();

  const pushWork = (work: RecordingWorkSummary) => {
    const key = work.id;
    if (seen.has(key)) {
      const existingIndex = works.findIndex((entry) => entry.id === key);
      if (existingIndex !== -1) {
        const current = works[existingIndex];
        const merged: RecordingWorkSummary = { ...current };
        if (!merged.name && work.name) {
          merged.name = work.name;
        }
        if (!merged.composerName && work.composerName) {
          merged.composerName = work.composerName;
        }
        if (!merged.composerId && work.composerId) {
          merged.composerId = work.composerId;
        }
        if (!merged.catalogLabel && work.catalogLabel) {
          merged.catalogLabel = work.catalogLabel;
        }
        if (!merged.catalogNumberNormalized && work.catalogNumberNormalized) {
          merged.catalogNumberNormalized = work.catalogNumberNormalized;
        }
        if (typeof work.confidence === 'number') {
          merged.confidence = work.confidence;
        }
        works[existingIndex] = merged;
      }
      return;
    }

    seen.add(key);
    works.push(work);
  };

  const workSources = [
    recording.works,
    recording.relatedWorks,
    recording.workList,
    recording.work_list,
    recording.matchedWorks,
    recording.matched_works,
    recording.work ? [recording.work] : [],
  ];

  for (const source of workSources) {
    if (!source) continue;
    const entries = Array.isArray(source) ? source : [source];

    for (const entry of entries) {
      if (!entry) continue;

      if (typeof entry === 'string') {
        const id = stringOrUndefined(entry);
        if (id) {
          pushWork({ id });
        }
        continue;
      }

      if (typeof entry !== 'object') {
        continue;
      }

      const record = entry as Record<string, any>;
      const composer = record.composer as Record<string, any> | undefined;

      const id =
        pickFirstString(record.id, record.workId, record.work_id, record.uuid, record.slug) ??
        undefined;
      if (!id) {
        continue;
      }

      const name = pickFirstString(
        record.name,
        record.title,
        record.displayName,
        record.display_name
      );

      const composerName =
        pickFirstString(
          record.composerName,
          record.composer_name,
          composer?.name,
          composer?.displayName,
          composer?.display_name
        ) ?? context.composerName;

      const composerId =
        pickFirstString(
          record.composerId,
          record.composer_id,
          composer?.id,
          composer?.composerId,
          composer?.composer_id,
          record.composerUuid,
          record.composer_uuid
        ) ?? undefined;

      const confidence = pickFirstNumber(
        record.confidence,
        record.matchConfidence,
        record.confidenceScore,
        record.score
      );

      const catalogLabel =
        pickFirstString(
          record.catalogLabel,
          record.catalog_label,
          record.catalogDisplay,
          record.catalog_display,
          record.displayLabel,
          record.display_label
        ) ?? undefined;

      const catalogNumberNormalized =
        pickFirstString(
          record.catalogNumberNormalized,
          record.catalog_number_normalized,
          record.catalogueNumberNormalized,
          record.catalogue_number_normalized,
          record.numberNormalized,
          record.number_normalized,
          record.baseNumber,
          record.base_number,
          (record.catalog ?? record.catalogue)?.numberNormalized,
          (record.catalog ?? record.catalogue)?.number_normalized
        ) ?? undefined;

      const summary: RecordingWorkSummary = { id };
      if (name) summary.name = name;
      if (composerName) summary.composerName = composerName;
      if (composerId) summary.composerId = composerId;
      if (typeof confidence === 'number') summary.confidence = confidence;
      if (catalogLabel) summary.catalogLabel = catalogLabel;
      if (catalogNumberNormalized) summary.catalogNumberNormalized = catalogNumberNormalized;

      pushWork(summary);
    }
  }

  const extractedWorkIds = extractWorkIds(recording);
  if (!works.length && extractedWorkIds.length) {
    const fallbackComposer = extractComposerName(recording, context);
    const fallbackName = extractWorkName(recording);
    pushWork({
      id: extractedWorkIds[0],
      name: fallbackName,
      composerName: fallbackComposer ?? context.composerName,
    });
  }

  return works;
};

const extractRecordingDescription = (recording: BackendRecording): string | undefined => {
  const direct = pickFirstString(
    recording.description,
    recording.summary,
    recording.notes,
    recording.subtitle,
    recording.overview
  );
  if (direct) {
    return direct;
  }

  const album = recording.album as Record<string, any> | undefined;
  if (album && typeof album === 'object') {
    return pickFirstString(album.description, album.summary, album.notes, album.overview);
  }

  return undefined;
};

const extractRecordingLabel = (recording: BackendRecording): string | undefined => {
  const direct = pickFirstString(
    recording.label,
    recording.labelName,
    recording.label_name,
    recording.recordLabel,
    recording.record_label,
    recording.publisher,
    recording.publisherName,
    recording.publisher_name
  );
  if (direct) {
    return direct;
  }

  const album = recording.album as Record<string, any> | undefined;
  if (album && typeof album === 'object') {
    return pickFirstString(
      album.label,
      album.labelName,
      album.label_name,
      album.recordLabel,
      album.record_label,
      album.publisher,
      album.publisherName,
      album.publisher_name
    );
  }

  return undefined;
};

const buildRecordingDetail = (
  recording: BackendRecording,
  context: { composerName?: string }
): RecordingDetail => {
  const normalized = normalizeRecording(recording, context);
  const tracks = normalizeTrackList(recording);
  const performers = normalizePerformerCollection(recording);
  const works = normalizeWorkSummaries(recording, context);
  const description = extractRecordingDescription(recording);
  const label = extractRecordingLabel(recording);

  const detail: RecordingDetail = {
    recording: normalized,
    tracks,
    performers,
    works,
    raw: recording,
  };

  if (description) {
    detail.description = description;
  }
  if (label) {
    detail.label = label;
  }

  return detail;
};

const normalizePagination = (payload: Record<string, any>, options: FetchRecordingsOptions): RecordingsPagination => {
  const total =
    numberOrUndefined(payload.total) ??
    numberOrUndefined(payload.count) ??
    numberOrUndefined(payload.totalCount) ??
    numberOrUndefined(payload.total_count) ??
    0;

  const limit =
    numberOrUndefined(payload.limit) ??
    numberOrUndefined(payload.pageSize) ??
    numberOrUndefined(payload.page_size) ??
    numberOrUndefined(options.limit) ??
    0;

  const offset =
    numberOrUndefined(payload.offset) ??
    numberOrUndefined(payload.start) ??
    numberOrUndefined(payload.page) ??
    numberOrUndefined(options.offset) ??
    0;

  const hasMore =
    typeof payload.hasMore === 'boolean'
      ? payload.hasMore
      : payload.has_more === true
      ? true
      : Boolean(limit && total && offset + limit < total);

  return {
    total: typeof total === 'number' ? total : 0,
    limit: typeof limit === 'number' ? limit : 0,
    offset: typeof offset === 'number' ? offset : 0,
    hasMore,
  };
};

const buildQueryParams = (options: FetchRecordingsOptions) => ({
  limit: options.limit,
  offset: options.offset,
  composerName: options.composerName,
  workName: options.workName,
});

const emptyResult = (options: Pick<FetchRecordingsOptions, 'limit' | 'offset'> = {}): RecordingListResult => ({
  recordings: [],
  pagination: {
    total: 0,
    limit: options.limit ?? 0,
    offset: options.offset ?? 0,
    hasMore: false,
  },
});

const deserializeRecordingList = (
  response: { recordings?: BackendRecording[]; pagination?: Record<string, any> } | undefined,
  context: { composerName?: string; workName?: string }
): LibraryRecording[] => {
  const rawRecordings = Array.isArray(response?.recordings) ? response!.recordings : [];
  if (rawRecordings.length) {
    // Log a few raw payloads to verify backend fields like proxyPopularity are present.
    const sample = rawRecordings.slice(0, 3).map((rec) => {
      return {
        id: (rec as Record<string, unknown>).id,
        proxyPopularity: (rec as Record<string, unknown>).proxyPopularity,
        proxy_popularity: (rec as Record<string, unknown>).proxy_popularity,
        averageTrackPopularity: (rec as Record<string, unknown>).averageTrackPopularity,
        avg_track_popularity: (rec as Record<string, unknown>).avg_track_popularity,
        artistIdsHash: (rec as Record<string, unknown>).artistIdsHash,
        artist_ids_hash: (rec as Record<string, unknown>).artist_ids_hash
      };
    });
    // eslint-disable-next-line no-console
    console.info('[Work recordings] backend payload sample', sample);
  }
  return rawRecordings.map((record) => normalizeRecording(record ?? {}, context));
};

const fetchRecordingDetailInternal = async (
  recordingId: string,
  options: FetchRecordingByIdOptions
): Promise<RecordingDetail | null> => {
  if (!recordingId) {
    return null;
  }

  const { data } = await backendClient.get<{
    recording?: BackendRecording;
    tracks?: BackendRecording[];
    relatedTracks?: BackendRecording[];
  }>(
    `/catalog/recordings/${encodeURIComponent(recordingId)}`
  );

  const payload = (data?.recording ?? data) as BackendRecording | undefined;
  if (!payload) {
    return null;
  }

  const detail = buildRecordingDetail(payload, {
    composerName: options.composerName,
  });

  if (!detail.tracks?.length) {
    const candidates = [
      Array.isArray(data?.tracks) ? data!.tracks : undefined,
      Array.isArray((payload as any)?.tracks) ? ((payload as any).tracks as BackendRecording[]) : undefined,
      Array.isArray(data?.relatedTracks) ? data!.relatedTracks : undefined,
    ];

    for (const collection of candidates) {
      if (!Array.isArray(collection) || !collection.length) continue;
      const hasObjectEntry = collection.some((entry) => entry && typeof entry === 'object');
      if (!hasObjectEntry) continue;
      const normalized = normalizeTrackList({ tracks: collection } as BackendRecording);
      if (normalized.length) {
        detail.tracks = normalized;
        break;
      }
    }

    if (!detail.tracks?.length && Array.isArray(payload.trackIds)) {
      detail.tracks = payload.trackIds
        .map((trackId: unknown, index: number) => {
          const id = stringOrUndefined(trackId);
          if (!id) {
            return null;
          }
          const track: RecordingTrackInfo = {
            id,
            name:
              (Array.isArray((payload as any)?.trackNames)
                ? stringOrUndefined((payload as any).trackNames[index])
                : undefined) ??
              (Array.isArray((payload as any)?.trackTitles)
                ? stringOrUndefined((payload as any).trackTitles[index])
                : undefined) ??
              ((typeof payload.trackCount === 'number' && payload.trackCount > 1)
                ? `Track ${index + 1}`
              : payload.recordingName ?? payload.name ?? id),
            position: index + 1,
          };
          const spotifyUri = pickSpotifyTrackUri(trackId, id);
          if (spotifyUri) {
            track.spotifyUri = spotifyUri;
            track.uri = spotifyUri;
            const match = spotifyUri.match(/^spotify:track:([0-9A-Za-z]{22})$/i);
            if (match) {
              track.spotifyTrackId = match[1];
            }
          }
          const durationMs = Array.isArray((payload as any).trackDurations)
            ? numberOrUndefined((payload as any).trackDurations[index])
            : undefined;
          if (typeof durationMs === 'number' && durationMs > 0) {
            track.durationLabel = msToTime(durationMs);
          }
          const performer = payload.performerSummary ?? payload.performerName ?? payload.performer_name;
          if (performer) {
            track.performerName = String(performer);
          }
          return track;
        })
        .filter((entry): entry is RecordingTrackInfo => !!entry);
    }
  }

  if (!detail.recording.performerName && payload.performerSummary) {
    detail.recording.performerName = payload.performerSummary;
  }
  if (!detail.recording.albumName && payload.albumName) {
    detail.recording.albumName = payload.albumName;
  }
  if (!detail.recording.imageUrl && payload.albumImageUrl) {
    detail.recording.imageUrl = payload.albumImageUrl;
  }

  const applyCatalogMetadata = (label?: string, normalized?: string) => {
    if (label && !detail.recording.workCatalogLabel) {
      detail.recording.workCatalogLabel = label;
    }
    if (normalized && !detail.recording.workCatalogNumberNormalized) {
      detail.recording.workCatalogNumberNormalized = normalized;
    }

    if ((label || normalized) && Array.isArray(detail.works) && detail.recording.workId) {
      detail.works = detail.works.map((work) => {
        if (work.id !== detail.recording.workId) {
          return work;
        }
        const next: RecordingWorkSummary = { ...work };
        if (label && !next.catalogLabel) {
          next.catalogLabel = label;
        }
        if (normalized && !next.catalogNumberNormalized) {
          next.catalogNumberNormalized = normalized;
        }
        return next;
      });
    }
  };

  if (!detail.recording.workCatalogLabel || !detail.recording.workCatalogNumberNormalized) {
    const summaryMatch = Array.isArray(detail.works)
      ? detail.works.find((work) => work.id === detail.recording.workId && (work.catalogLabel || work.catalogNumberNormalized))
      : undefined;
    if (summaryMatch) {
      applyCatalogMetadata(summaryMatch.catalogLabel, summaryMatch.catalogNumberNormalized);
    }
  }

  if (detail.recording.workId && (!detail.recording.workCatalogLabel || !detail.recording.workCatalogNumberNormalized)) {
    try {
      const workDetail = await worksService.fetchWorkById(detail.recording.workId);
      if (workDetail) {
        const label =
          (workDetail as any).catalogLabel ??
          workDetail.catalogueNumber ??
          workDetail.catalogueNumberNormalized ??
          undefined;
        const normalizedNumber = workDetail.catalogueNumberNormalized ?? workDetail.catalogueNumber ?? undefined;
        applyCatalogMetadata(label, normalizedNumber);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[recordingsService] Failed to enrich recording catalog metadata', {
          recordingId,
          workId: detail.recording.workId,
          error,
        });
      }
    }
  }

  return detail;
};

export const recordingsService = {
  async fetchComposerRecordings(
    composerId: string,
    options: FetchComposerRecordingsOptions = {}
  ): Promise<LibraryRecording[]> {
    if (!composerId) {
      return [];
    }

    try {
      const { data } = await backendClient.get<{ recordings?: BackendRecording[] }>(
        `/catalog/composers/${encodeURIComponent(composerId)}/recordings`,
        {
          params: {
            limit: options.limit,
            offset: options.offset,
          },
        }
      );

      const rawRecordings = Array.isArray(data?.recordings) ? data.recordings : [];
      return rawRecordings.map((entry) =>
        normalizeRecording(entry ?? {}, { composerName: options.composerName })
      );
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        return [];
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn('[recordingsService] fetchComposerRecordings failed', error);
      }
      throw error;
    }
  },

  async fetchAllRecordingsByWork(
    workId: string,
    options: FetchRecordingsOptions = {}
  ): Promise<LibraryRecording[]> {
    if (!workId) {
      return [];
    }

    const pageSize = clampPositiveInteger(options.limit, { min: 20, max: 100, fallback: 100 });
    const collected: LibraryRecording[] = [];
    let offset = clampPositiveInteger(options.offset, { min: 0, max: Number.MAX_SAFE_INTEGER, fallback: 0 });
    let iterations = 0;
    const maxIterations = 40;

    while (iterations < maxIterations) {
      iterations += 1;
      const { recordings, pagination } = await this.fetchRecordingsByWork(workId, {
        composerName: options.composerName,
        workName: options.workName,
        limit: pageSize,
        offset,
      });

      if (recordings.length) {
        collected.push(...recordings);
      }

      const reachedEnd = !pagination.hasMore || recordings.length === 0;
      if (reachedEnd) {
        break;
      }

      const nextOffsetCandidate =
        typeof pagination.offset === 'number' && typeof pagination.limit === 'number' && pagination.limit > 0
          ? pagination.offset + pagination.limit
          : offset + pageSize;

      if (!Number.isFinite(nextOffsetCandidate) || nextOffsetCandidate <= offset) {
        break;
      }

      offset = nextOffsetCandidate;
    }

    return collected;
  },

  async fetchRecordingsByWork(
    workId: string,
    options: FetchRecordingsOptions = {}
  ): Promise<RecordingListResult> {
    if (!workId) {
      return emptyResult(options);
    }

    try {
      const { data } = await backendClient.get<{ recordings?: BackendRecording[]; pagination?: Record<string, any> }>(
        `/catalog/works/${encodeURIComponent(workId)}/recordings`,
        {
          params: buildQueryParams(options),
        }
      );

      const recordings = deserializeRecordingList(data, {
        composerName: options.composerName,
        workName: options.workName,
      });

      const pagination = normalizePagination(data?.pagination ?? {}, options);

      return { recordings, pagination };
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        return emptyResult(options);
      }
      if (process.env.NODE_ENV === 'development') {
        console.warn('[recordingsService] fetchRecordingsByWork failed', error);
      }
      throw error;
    }
  },

  async fetchRecordingsBatch(recordingIds: string[]): Promise<BackendRecording[]> {
    const normalized = Array.from(
      new Set(
        recordingIds
          .map((id) => stringOrUndefined(id))
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      )
    );

    if (!normalized.length) {
      return [];
    }

    try {
      const { data } = await backendClient.post<{ recordings?: BackendRecording[] }>(
        '/catalog/recordings/batch',
        { recordingIds: normalized }
      );

      const recordings = Array.isArray(data?.recordings) ? data.recordings : [];
      if (process.env.NODE_ENV === 'development' && recordings.length > 0) {
        const sample = recordings[0];
        try {
          // Lightweight visibility into payload shape/size in the browser console.
          // eslint-disable-next-line no-console
          console.info('[recordingsService:batch] sample', {
            id: sample?.id ?? sample?.recordingId,
            keys: Object.keys(sample ?? {}),
            sizeBytes: new TextEncoder().encode(JSON.stringify(sample)).length,
            count: recordings.length,
            sample,
            sampleWorks: Array.isArray((sample as any)?.works) ? (sample as any).works.slice(0, 2) : undefined
          });
        } catch {
          // Best-effort logging only.
        }
      }

      return recordings;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        return [];
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn('[recordingsService] fetchRecordingsBatch failed', error);
      }
      throw error;
    }
  },

  async fetchRecordingById(
    recordingId: string,
    options: FetchRecordingByIdOptions = {}
  ): Promise<LibraryRecording | null> {
    if (!recordingId) {
      return null;
    }

    try {
      const detail = await fetchRecordingDetailInternal(recordingId, options);
      return detail?.recording ?? null;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        return null;
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn('[recordingsService] fetchRecordingById failed', error);
      }
      throw error;
    }
  },

  async fetchRecordingDetail(
    recordingId: string,
    options: FetchRecordingByIdOptions = {}
  ): Promise<RecordingDetail | null> {
    if (!recordingId) {
      return null;
    }

    try {
      return await fetchRecordingDetailInternal(recordingId, options);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        return null;
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn('[recordingsService] fetchRecordingDetail failed', error);
      }
      throw error;
    }
  },

  async fetchRecordingsLastPlayed(recordingIds: string[]): Promise<Map<string, number>> {
    const result = new Map<string, number>();
    if (!Array.isArray(recordingIds) || recordingIds.length === 0) {
      return result;
    }

    const uniqueIds = Array.from(
      new Set(
        recordingIds
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id) => id.length > 0)
      )
    ).slice(0, 500);

    if (uniqueIds.length === 0) {
      return result;
    }

    const parseTimestamp = (value: unknown): number | null => {
      if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      if (value instanceof Date) {
        const ms = value.valueOf();
        return Number.isFinite(ms) ? ms : null;
      }
      return null;
    };

    try {
      const { data } = await backendClient.post<{ items?: Array<{ recordingId?: string; lastPlayedAt?: string }> }>(
        '/library/recordings/last-played',
        { recordingIds: uniqueIds }
      );
      const items = Array.isArray(data?.items) ? data.items : [];
      for (const entry of items) {
        const recordingId =
          typeof entry?.recordingId === 'string' ? entry.recordingId.trim() : undefined;
        const ts = parseTimestamp(entry?.lastPlayedAt);
        if (recordingId && ts !== null) {
          result.set(recordingId, ts);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[recordingsService] fetchRecordingsLastPlayed failed', error);
      }
    }

    return result;
  },

  async fetchTrackMatches(trackIds: string[]): Promise<Record<string, TrackMatchSummary>> {
    const LOG_PREFIX = '[trackMatchCache]';
    const logCache = (message: string, payload?: Record<string, unknown>) => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log(LOG_PREFIX, message, payload ?? {});
      }
    };

    const normalized = Array.from(
      new Set(
        trackIds
          .map((id) => stringOrUndefined(id))
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      )
    );

    if (!normalized.length) {
      return {};
    }

    const TRACK_MATCH_CACHE_PREFIX = 'cmf.trackMatch.';
    const TRACK_MATCH_CACHE_VERSION = 1;
    const TRACK_MATCH_CACHE_TTL_MS = 14 * 24 * 60 * 60 * 1000;
    const TRACK_MATCH_CACHE_JITTER_MS = 24 * 60 * 60 * 1000;
    const TRACK_MATCH_CACHE_MAX_ENTRIES = 3000;
    const canUseLocalStorage = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

    const randomJitter = (): number => Math.floor(Math.random() * TRACK_MATCH_CACHE_JITTER_MS);

    const makeCacheKey = (trackId: string): string => `${TRACK_MATCH_CACHE_PREFIX}${trackId}`;

    const readCachedMatch = (trackId: string): TrackMatchSummary | null => {
      if (!canUseLocalStorage) return null;
      try {
        const raw = localStorage.getItem(makeCacheKey(trackId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
          localStorage.removeItem(makeCacheKey(trackId));
          return null;
        }
        if (parsed.version !== TRACK_MATCH_CACHE_VERSION) {
          localStorage.removeItem(makeCacheKey(trackId));
          return null;
        }
        const expiresAt = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0;
        if (!expiresAt || Date.now() > expiresAt) {
          localStorage.removeItem(makeCacheKey(trackId));
          return null;
        }
        return parsed.data as TrackMatchSummary;
      } catch {
        localStorage.removeItem(makeCacheKey(trackId));
        return null;
      }
    };

    const pruneCacheIfNeeded = () => {
      if (!canUseLocalStorage) return;
      try {
        const keys: Array<{ key: string; expiresAt: number }> = [];
        const now = Date.now();
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (!key || !key.startsWith(TRACK_MATCH_CACHE_PREFIX)) continue;
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          try {
            const parsed = JSON.parse(raw);
            const expiresAt = typeof parsed?.expiresAt === 'number' ? parsed.expiresAt : 0;
            if (!expiresAt || expiresAt <= now || parsed?.version !== TRACK_MATCH_CACHE_VERSION) {
              localStorage.removeItem(key);
              continue;
            }
            keys.push({ key, expiresAt });
          } catch {
            localStorage.removeItem(key);
          }
        }

        if (keys.length > TRACK_MATCH_CACHE_MAX_ENTRIES) {
          const excess = keys.length - TRACK_MATCH_CACHE_MAX_ENTRIES;
          keys.sort((a, b) => a.expiresAt - b.expiresAt);
          for (let index = 0; index < excess; index += 1) {
            localStorage.removeItem(keys[index].key);
          }
        }
      } catch {
        // Ignore cache cleanup failures
      }
    };

    const writeCachedMatch = (trackId: string, match: TrackMatchSummary) => {
      if (!canUseLocalStorage) return;
      try {
        const payload = {
          version: TRACK_MATCH_CACHE_VERSION,
          expiresAt: Date.now() + TRACK_MATCH_CACHE_TTL_MS + randomJitter(),
          data: match,
        };
        pruneCacheIfNeeded();
        localStorage.setItem(makeCacheKey(trackId), JSON.stringify(payload));
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[recordingsService] track-match cache write failed', { trackId, error });
        }
      }
    };

    const result: Record<string, TrackMatchSummary> = {};
    const missing: string[] = [];
    let hitCount = 0;
    let fetchedCount = 0;

    for (const trackId of normalized) {
      const cached = readCachedMatch(trackId);
      if (cached) {
        result[trackId] = cached;
        hitCount += 1;
      } else {
        missing.push(trackId);
      }
    }

    if (!missing.length) {
      logCache('all hits; no fetch needed', { total: normalized.length, hits: hitCount });
      return result;
    }

    logCache('cache miss fetch start', {
      total: normalized.length,
      hits: hitCount,
      missing: missing.length,
    });

    const batches: string[][] = [];
    const BATCH_SIZE = 200;
    for (let index = 0; index < missing.length; index += BATCH_SIZE) {
      batches.push(missing.slice(index, index + BATCH_SIZE));
    }

    const parseEntries = (entries: any[]) => {
      for (const entry of entries) {
        if (!entry || typeof entry !== 'object') continue;
        const candidate = entry as Record<string, unknown>;

        const spotifyTrackId = stringOrUndefined(candidate.spotifyTrackId ?? candidate.spotify_track_id);
        if (!spotifyTrackId) {
          continue;
        }

        const match: TrackMatchSummary = {
          spotifyTrackId,
        };

        const workId = stringOrUndefined(candidate.workId ?? candidate.work_id);
        if (workId) {
          match.workId = workId;
        }

        const workName = stringOrUndefined(candidate.workName ?? candidate.work_name);
        if (workName) {
          match.workName = workName;
        }

        const composerId = stringOrUndefined(candidate.composerId ?? candidate.composer_id);
        if (composerId) {
          match.composerId = composerId;
        }

        const composerName = stringOrUndefined(candidate.composerName ?? candidate.composer_name);
        if (composerName) {
          match.composerName = composerName;
        }

        const spotifyAlbumId = stringOrUndefined(candidate.spotifyAlbumId ?? candidate.spotify_album_id);
        if (spotifyAlbumId) {
          match.spotifyAlbumId = spotifyAlbumId;
        }

        const trackNumber = numberOrUndefined(candidate.trackNumber ?? candidate.track_number);
        if (typeof trackNumber === 'number') {
          match.trackNumber = trackNumber;
        }

        const confidence = numberOrUndefined(candidate.confidence);
        if (typeof confidence === 'number') {
          match.confidence = confidence;
        }

        const recordingId = stringOrUndefined(candidate.recordingId ?? candidate.recording_id);
        if (recordingId) {
          match.recordingId = recordingId;
        }

        const recordingName = stringOrUndefined(candidate.recordingName ?? candidate.recording_name);
        if (recordingName) {
          match.recordingName = recordingName;
        }

        const albumName = stringOrUndefined(candidate.albumName ?? candidate.album_name);
        if (albumName) {
          match.albumName = albumName;
        }

        const albumImageUrl = stringOrUndefined(candidate.albumImageUrl ?? candidate.album_image_url);
        if (albumImageUrl) {
          match.albumImageUrl = albumImageUrl;
        }

        const performerName = stringOrUndefined(candidate.performerName ?? candidate.performer_name);
        if (performerName) {
          match.performerName = performerName;
        }

        result[spotifyTrackId] = match;
        writeCachedMatch(spotifyTrackId, match);
        fetchedCount += 1;
      }
    };

    try {
      for (const batch of batches) {
        try {
          logCache('requesting batch', { size: batch.length, batches: batches.length });
          const { data } = await backendClient.get<{ matches?: any[] }>(
            '/catalog/recordings/track-matches',
            {
              params: {
                spotifyTrackIds: batch.join(','),
              },
            }
          );
          parseEntries(Array.isArray(data?.matches) ? data.matches : []);
        } catch (batchError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[recordingsService] fetchTrackMatches batch failed', batchError);
          }
        }
      }

      const unresolved = missing.filter((trackId) => !result[trackId]);
      if (unresolved.length) {
        logCache('marking not-found cache entries', { unresolved: unresolved.length });
        for (const trackId of unresolved) {
          const placeholder: TrackMatchSummary = { spotifyTrackId: trackId };
          result[trackId] = placeholder;
          writeCachedMatch(trackId, placeholder);
        }
      }

      logCache('cache fetch complete', {
        total: normalized.length,
        hits: hitCount,
        fetched: fetchedCount,
        missingAfterFetch: missing.length - fetchedCount,
        unresolvedCached: unresolved.length,
      });
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[recordingsService] fetchTrackMatches failed', error);
      }
      return result;
    }
  },
};
